import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { downloadFile } from '../../../slices/thunks';
import GuideHeaderCard from './GuideHeaderCard';
import StatusGuideBadge, { OutlineSemanticBadge } from './StatusGuideBadge';
import TraceabilityModal from '../Traceability/TraceabilityModal';
import PdfPreviewModal from '../../../Components/General/Modal/PdfPreviewModal';
import {
	EstablishmentNameFilter,
	ResidueNameFilter,
	StatusGuideNameFilter,
	ResidueCategoryNamesFilter,
	DestinationNameFilter,
} from './GuidesFlatColumnFilters';
import { RESIDUE_GUIDE_EDIT_ENABLED } from '../legacy/constants';

const RESIDUE_CATEGORY_BADGE_VARIANT = {
	Peligrosos: 'danger',
	'No Peligrosos': 'success',
	'Limpieza de Playa': 'info',
};

/**
 * Tabla remota: filtros OData por columna + paginación/sort en servidor.
 * `getDataFunc` debe devolver `{ data, meta }` (p. ej. con filas agrupadas visualmente).
 */
const GuidesFlatGrid = ({ getDataFunc }) => {
	const dispatch = useDispatch();
	const [traceModalOpen, setTraceModalOpen] = useState(false);
	const [traceGuideId, setTraceGuideId] = useState(null);
	const [pdfPreview, setPdfPreview] = useState({
		open: false,
		blob: null,
		title: '',
	});
	const [pdfLoadingDocId, setPdfLoadingDocId] = useState(null);

	const toggleTraceModal = useCallback(() => {
		setTraceModalOpen(prev => {
			if (prev) {
				setTraceGuideId(null);
			}
			return !prev;
		});
	}, []);

	const openGuideTraceability = useCallback(guideId => {
		if (!guideId) return;
		setTraceGuideId(guideId);
		setTraceModalOpen(true);
	}, []);

	const handleClickPdf = useCallback(
		async row => {
			const { document_id: documentId, folio } = row;
			if (!documentId) {
				toast.error('Esta guía no tiene documento PDF asociado');
				return;
			}
			try {
				setPdfLoadingDocId(documentId);
				const result = await dispatch(
					downloadFile({ file_id: documentId, disable_msg: true }),
				);
				const blob = result.payload;
				if (!(blob instanceof Blob)) {
					throw new Error('No se pudo obtener el archivo');
				}
				setPdfPreview({
					open: true,
					blob,
					title: `Guía de Despacho #${folio ?? ''}`,
				});
			} catch (e) {
				console.error('Error al obtener el PDF:', e);
				toast.error(
					`Error al cargar el PDF de la guía #${folio ?? ''}`,
				);
			} finally {
				setPdfLoadingDocId(null);
			}
		},
		[dispatch],
	);

	const closePdfPreview = useCallback(() => {
		setPdfPreview({ open: false, blob: null, title: '' });
	}, []);

	const columns = useMemo(
		() => [
			{
				header: '',
				accessorKey: '_isFirstInGroup',
				size: 0,
				enableColumnFilter: false,
				Cell: ({ row }) => {
					if (row.original._isFirstInGroup) {
						return (
							<td colSpan={10} className="p-0 border-0">
								<GuideHeaderCard item={row.original} />
							</td>
						);
					}
					return null;
				},
			},
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: ({ row }) => {
					const r = row.original;
					const { guide_id, document_id: documentId } = r;
					const hasPdf = !!documentId;
					const isLoadingThis = pdfLoadingDocId && pdfLoadingDocId === documentId;
					return (
						<div className="d-flex">
							<div className="flex-shrink-0">
								<ul className="list-inline mb-0">
									<li className="list-inline-item">
										<Link
											to="#"
											onClick={e => {
												e.preventDefault();
												if (hasPdf) handleClickPdf(r);
											}}
											title={
												hasPdf
													? 'Ver PDF'
													: 'Sin documento PDF'
											}
											style={{
												opacity: hasPdf ? 1 : 0.3,
												cursor: hasPdf
													? 'pointer'
													: 'not-allowed',
											}}
										>
											{isLoadingThis ? (
												<div
													className="spinner-border spinner-border-sm"
													role="status"
													style={{ width: 14, height: 14 }}
												>
													<span className="visually-hidden">
														Cargando...
													</span>
												</div>
											) : (
												<i className="ri-file-pdf-line align-bottom me-2 text-muted" />
											)}
										</Link>
									</li>
									<li className="list-inline-item">
										<Link
											to="#"
											title="Trazabilidad de la guía y la cadena relacionada"
											onClick={e => {
												e.preventDefault();
												openGuideTraceability(guide_id);
											}}
										>
											<i className="ri-git-branch-line align-bottom me-2 text-primary fs-16" />
										</Link>
									</li>
								</ul>
							</div>
						</div>
					);
				},
			},
			{
				header: 'Guía',
				accessorKey: 'folio',
				dataType: 'string',
				size: 'auto',
				Cell: ({ row }) => {
					const displayNumber =
						row.original.folio ?? row.original.guide_number ?? '-';
					const gid = row.original.guide_id;
					if (!gid || displayNumber === '-' || !RESIDUE_GUIDE_EDIT_ENABLED) {
						return (
							<span className="fw-bold text-primary">
								{displayNumber}
							</span>
						);
					}
					return (
						<Link
							to={`/residue/guides/${gid}`}
							className="fw-bold text-primary"
							style={{ textDecoration: 'none' }}
							title={`Ver guía ${displayNumber}`}
						>
							{displayNumber}
						</Link>
					);
				},
			},
			{
				header: 'Fecha',
				accessorKey: 'guide_date',
				dataType: 'date',
				size: 100,
				Cell: ({ row }) => {
					const date = row.original.guide_date;
					if (!date) return '-';
					return new Date(date).toLocaleDateString('es-CL', {
						day: '2-digit',
						month: '2-digit',
						year: 'numeric',
					});
				},
			},
			{
				header: 'Estado',
				accessorKey: 'status_guide_name',
				dataType: 'select',
				Filter: StatusGuideNameFilter,
				size: 120,
				Cell: ({ row }) => {
					const { status_guide_name, status_guide_txcolor } =
						row.original;
					return (
						<StatusGuideBadge
							name={status_guide_name}
							txColor={status_guide_txcolor}
						/>
					);
				},
			},
			{
				header: 'Origen',
				accessorKey: 'establishment_name',
				dataType: 'select',
				Filter: EstablishmentNameFilter,
				size: 150,
			},
			{
				header: 'Destino',
				id: 'destino_pick',
				accessorFn: row =>
					row.final_destination_name || row.warehouse_name || '-',
				dataType: 'select',
				Filter: DestinationNameFilter,
				size: 150,
			},
			{
				header: 'Residuo',
				id: 'residue_id',
				accessorFn: row =>
					`${row.residue_name || '-'} ${row.residue_ler ? `(LER: ${row.residue_ler})` : ''}`,
				dataType: 'select',
				Filter: ResidueNameFilter,
				size: 200,
			},
			{
				header: 'Categoría',
				id: 'residue_category_id',
				accessorKey: 'residue_category_name',
				dataType: 'multi-select',
				Filter: ResidueCategoryNamesFilter,
				size: 120,
				Cell: ({ row }) => {
					const category = row.original.residue_category_name;
					return (
						<OutlineSemanticBadge
							label={category}
							variant={
								RESIDUE_CATEGORY_BADGE_VARIANT[category] ||
								'secondary'
							}
						/>
					);
				},
			},
			{
				header: 'KG Ítem',
				accessorKey: 'kilograms_total_item',
				dataType: 'float.2',
				size: 100,
				enableColumnFilter: false,
				Cell: ({ row }) => {
					const kg = row.original.kilograms_total_item;
					if (kg === null || kg === undefined) return '-';
					return (
						<span className="fw-semibold text-end d-block">
							{kg.toLocaleString('es-CL', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
					);
				},
			},
			{
				header: 'KG Total Guía',
				accessorKey: 'kilograms_total_guide',
				dataType: 'float.2',
				size: 120,
				enableColumnFilter: false,
				Cell: ({ row }) => {
					const kg = row.original.kilograms_total_guide;
					if (kg === null || kg === undefined) return '-';
					return (
						<span className="text-muted text-end d-block">
							{kg.toLocaleString('es-CL', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</span>
					);
				},
			},
		],
		[handleClickPdf, openGuideTraceability, pdfLoadingDocId],
	);

	const dataColumns = columns.filter(
		col => col.accessorKey !== '_isFirstInGroup',
	);

	return (
		<>
			<ODataGrid
				columns={dataColumns}
				remoteOperations={{
					pagination: true,
					sorting: true,
					filtering: true,
				}}
				getDataFunc={getDataFunc}
				defaultPageSize={100}
				searching={false}
				filtering
				sorting
				getRowIdTable={row => row.guide_detail_id || String(row._groupIndex)}
				className="guides-flat-grid"
			/>

			<TraceabilityModal
				isOpen={traceModalOpen}
				toggle={toggleTraceModal}
				entityId={traceGuideId}
			/>

			<PdfPreviewModal
				isOpen={pdfPreview.open}
				onClose={closePdfPreview}
				blob={pdfPreview.blob}
				title={pdfPreview.title}
			/>
		</>
	);
};

export default GuidesFlatGrid;
